/**
 * Gated HITL bridge for `clear_contours` — the one agent operation that
 * edits an existing structure IN PLACE, with the same three choices as the
 * Clear contours operation in the right-hand operations panel (current
 * slice / all / keep every Nth).
 *
 * Flow: parse the model's tool input → resolve the target with the shared
 * resolver (point / empty / ambiguous / not-found guards) → refuse approved
 * structures → a {@link ClearContoursProposal} the card renders. Nothing is
 * cleared until the clinician Applies. There is no geometry to preview, so
 * the card states exactly what will be cleared instead; for `current-slice`
 * the slice position is captured when the card is built and Apply clears
 * THAT slice, even if the clinician has scrolled since.
 *
 * Safety mirrors the creating ops' shared gate: ≤1-pending preamble, a
 * structure-set switch between card and Apply is refused, and approval is
 * re-checked at Apply. Pure/React-free; the engine is injected.
 */
// The panel's own minimum for "keep every Nth slice" — one source.
import { minSliceInterval as MIN_KEEP_EVERY_NTH_SLICE } from '~/state/atoms/toolsAtoms';
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    reasonOf,
    resolveFailureToPlan,
    resolveOneStructure,
    toListed,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
    type StructureFacts,
} from '../core';
import type { ClearContoursInput, ClearContoursOutcome } from '../../contract';

export type ClearScope = ClearContoursInput['scope'];

const SCOPES: ReadonlySet<string> = new Set<ClearScope>([
    'current-slice',
    'all',
    'keep-every-nth',
]);

/** The engine calls the panel uses, injected (faked in unit tests). */
export interface ClearContoursDeps extends PendingFlag {
    readonly structures: ReadonlyArray<StructureFacts>;
    /** False for an approved structure (the panel refuses those too). */
    canModify(roiNumber: number): boolean;
    /** Position (mm) of the slice shown in the main view, as the panel reads
     *  it; undefined when no main view is available. */
    currentSlicePositionMm(): number | undefined;
    /** Identity of the active structure set, read live. */
    currentContextId(): string | undefined;
    /** The structure's current volume (cm³), read live. */
    volumeCm3(roiNumber: number): number;
    readonly clear: {
        slice(roiNumber: number, slicePositionMm: number): void;
        all(roiNumber: number): void;
        keepEveryNth(roiNumber: number, n: number): void;
    };
}

export interface ClearContoursProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    readonly scope: ClearScope;
    /** Set for `keep-every-nth`. */
    readonly keepEveryNth?: number;
    /** Set for `current-slice`: the slice that Apply clears (mm). */
    readonly slicePositionMm?: number;
    apply(): Promise<ClearContoursOutcome>;
    reject(): ClearContoursOutcome;
    /** No geometry overlay for a clear; kept for the shared channel. */
    showPreview(): void;
    hidePreview(): void;
}

export type ClearContoursPlan = MutatingPlan<ClearContoursProposal>;

interface ParsedInput {
    readonly roi: string;
    readonly scope: ClearScope;
    readonly keepEveryNth?: number;
}

/** Defensively read the model-supplied (untyped) tool input. */
function readInput(input: unknown): ParsedInput | string {
    const o = asRecord(input);
    if (
        o === undefined ||
        typeof o.roi !== 'string' ||
        typeof o.scope !== 'string' ||
        !SCOPES.has(o.scope)
    ) {
        return 'invalid clear_contours input';
    }
    const scope = o.scope as ClearScope;
    if (scope !== 'keep-every-nth') {
        return { roi: o.roi, scope };
    }
    const n = o.keepEveryNth;
    if (
        typeof n !== 'number' ||
        !Number.isInteger(n) ||
        n < MIN_KEEP_EVERY_NTH_SLICE
    ) {
        return (
            'keep-every-nth needs keepEveryNth, an integer of at least ' +
            `${MIN_KEEP_EVERY_NTH_SLICE}`
        );
    }
    return { roi: o.roi, scope, keepEveryNth: n };
}

function notModifiable(name: string): string {
    return `'${name}' is approved and cannot be modified`;
}

export async function planClearContoursToolCall(
    call: MutatingToolCall,
    deps: ClearContoursDeps
): Promise<ClearContoursPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }

    const parsed = readInput(call.input);
    if (typeof parsed === 'string') {
        return failedPlan(call.toolCallId, parsed);
    }

    const resolved = resolveOneStructure(
        parsed.roi,
        deps.structures,
        deps.structures.map(toListed),
        'contour clear',
        'clear'
    );
    if (resolved.kind !== 'source') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }
    const source = resolved.source;
    if (!deps.canModify(source.roiNumber)) {
        return failedPlan(call.toolCallId, notModifiable(source.name));
    }

    let slicePositionMm: number | undefined;
    if (parsed.scope === 'current-slice') {
        slicePositionMm = deps.currentSlicePositionMm();
        if (
            slicePositionMm === undefined ||
            !Number.isFinite(slicePositionMm)
        ) {
            return failedPlan(
                call.toolCallId,
                'no main view is available to tell which slice is shown'
            );
        }
    }

    // The structure set this card was built against; a switch before Apply
    // is refused rather than clearing a same-numbered ROI in another set.
    const plannedContextId = deps.currentContextId();
    const { roiNumber, name } = source;
    const volumeBeforeCm3 = source.volumeCm3;

    const proposal: ClearContoursProposal = {
        toolCallId: call.toolCallId,
        target: { roiNumber, name, currentVolumeCm3: volumeBeforeCm3 },
        scope: parsed.scope,
        ...(parsed.keepEveryNth !== undefined
            ? { keepEveryNth: parsed.keepEveryNth }
            : {}),
        ...(slicePositionMm !== undefined ? { slicePositionMm } : {}),
        apply: async () => {
            if (deps.currentContextId() !== plannedContextId) {
                return {
                    status: 'failed',
                    reason:
                        'the active structure set changed before this was ' +
                        'applied; re-issue the request against the current set',
                };
            }
            // Approval can change while the card waits.
            if (!deps.canModify(roiNumber)) {
                return { status: 'failed', reason: notModifiable(name) };
            }
            try {
                if (parsed.scope === 'all') {
                    deps.clear.all(roiNumber);
                } else if (parsed.scope === 'keep-every-nth') {
                    deps.clear.keepEveryNth(
                        roiNumber,
                        parsed.keepEveryNth as number
                    );
                } else {
                    deps.clear.slice(roiNumber, slicePositionMm as number);
                }
            } catch (err) {
                return { status: 'failed', reason: reasonOf(err) };
            }
            const volumeAfterCm3 = deps.volumeCm3(roiNumber);
            if (!Number.isFinite(volumeAfterCm3)) {
                return {
                    status: 'failed',
                    reason:
                        'contours were cleared but the new volume could not ' +
                        'be read; check the structure in the viewer',
                };
            }
            return {
                status: 'applied',
                roiNumber,
                roi: name,
                volumeCm3: volumeAfterCm3,
                volumeDeltaCm3: volumeAfterCm3 - volumeBeforeCm3,
            };
        },
        reject: () => ({ status: 'rejected' }),
        showPreview: () => undefined,
        hidePreview: () => undefined,
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
